import { Pagination } from './../function/paginate';
import { CompanyListMapItem, CompanyListPageSearchResults } from './../typings/backend-models';
import cardsPagination from './pagination';

const getData = async (url: string) => {
    const res = await fetch(url).then((response) => response.json());
    return res;
};

const createListItem = (properties: CompanyListMapItem) => {
    const { name, address, phoneNumber, contentUrl } = properties;

    const articleEl = document.createElement('article');
    articleEl.classList.add('company-block-item');

    const content = `
        <div class="company__content">
            <div class="company__content__1">
                <a class="company__title" href="${contentUrl}">
                    <h3>${name}</h3>
                </a>
            </div>
            <div class="company__content__3">
                <div class="company__contact-wrapper">
                    ${phoneNumber ? `<a href="tel:${phoneNumber}" class="company__phone entryAttribute">${phoneNumber}</a>` : ''}
                </div>
            </div>
            ${address ? `<div class="company__address visiting__address entryAttribute"><p>${address}</p></div>` : ''}
        </div>
    `;
    articleEl.innerHTML = content;
    return articleEl;
};

const createList = (elements: CompanyListMapItem[]) => {
    const companyListContainer = document.querySelector('.company-list-container');
    if (!companyListContainer) return;
    
    // Remove existing company items
    const existingItems = companyListContainer.querySelectorAll('.company-block-item');
    existingItems.forEach(item => item.remove());

    elements.forEach((element) => {
        const listItem = createListItem(element);
        companyListContainer.appendChild(listItem);
    });
};

export const filterResults = (list: CompanyListMapItem[], searchData: CompanyListPageSearchResults) => {
    const { pageSearchHits } = searchData;
    const resultIds = pageSearchHits.map((item) => item.contentLinkId);
    const newList = list.filter((item) => resultIds.includes(item.contentLinkId));

    return newList;
};

export const getUrlData = (): { catIds: number[]; searchTerm: string } => {
    const catIds = [];
    let searchTerm: string = '';
    const url = new URL(window.location.href);

    for (const [key, value] of url.searchParams.entries()) {
        if (key === 'catids') {
            if (value != null) {
                const commaSeparatedValuesOrNot = value.split(",");
                for (const val of commaSeparatedValuesOrNot) {
                    catIds.push(Number(val));
                }
            } 
        } else if (key === 'q') {
            searchTerm = value;
        }
    }

    return { catIds, searchTerm };
};

export const getSearchResults = async ({
    guuid,
    langId,
    catIds,
    searchTerm,
}: {
    guuid: string;
    langId: number;
    catIds: number[];
    searchTerm: string;
}): Promise<CompanyListPageSearchResults> => {
    const mainUrl: string = window.location.origin;
    const apiUrl = `${mainUrl}/api/map/CompanyListPageSearchResults/${guuid}/${langId}?catIds=${catIds.toString()}&q=${searchTerm}`;
    const data = await getData(apiUrl);
    return data;
};

const updateList = async ({
    searchTerm,
    guuid,
    langId,
    initialList,
}: {
    searchTerm: string;
    guuid: string;
    langId: number;
    initialList: CompanyListMapItem[];
}) => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;
    searchParams.delete('q');
    searchParams.append('q', searchTerm);
    url.search = searchParams.toString();
    history.pushState({}, '', url.toString());

    const { catIds } = getUrlData();
    const searchData = await getSearchResults({ guuid, langId, catIds, searchTerm });
    const filteredList = filterResults(initialList, searchData);
    searchData.pageSearchHitsCount = filteredList.length;

    const paginationEl = document.querySelector('.pagination-dynamic');
    if (paginationEl) {
        const paginationWrapperEl = paginationEl.querySelector('.pagination__wrapper');
        if (paginationWrapperEl) {
            paginationWrapperEl.innerHTML = '';
        }
    }

    cardsPagination(searchData, (pagination) => handlePageChange(pagination, filteredList));
    handleNoResultsMessage(filteredList.length < 1);
    updateTotalHits(filteredList.length);
};

const clearList = (): void => {
    const companyListContainer = document.querySelector('.company-list-container');
    if (!companyListContainer) return;
    
    const existingItems = companyListContainer.querySelectorAll('.company-block-item');
    existingItems.forEach(item => item.remove());
};

const handlePageChange = (paginationData: Pagination, filteredList: CompanyListMapItem[]) => {
    const { startIndex, endIndex } = paginationData;
    const slicedList = filteredList.slice(startIndex, endIndex + 1);

    clearList();
    createList(slicedList);
};

const handleNoResultsMessage = (isVisible: boolean) => {
    let noResultsDiv = document.getElementById("no-results-placeholder");
    const companyListContainer = document.querySelector('.company-list-container');
    
    if (noResultsDiv && companyListContainer) {
        if (isVisible) {
            noResultsDiv.style.display = "block";
            (companyListContainer as HTMLElement).style.display = "none";
        } else {
            noResultsDiv.style.display = "none";
            (companyListContainer as HTMLElement).style.display = "";
        }
    }
}

export const updateTotalHits = (count: number) => {
    const totalHitsEl = document.querySelector('.totalHits') as HTMLElement | null;
    if (totalHitsEl) {
        const pluralText = totalHitsEl.dataset.hitsText ?? 'treff';
        const singularText = totalHitsEl.dataset.hitsTextSingular ?? pluralText;
        const hitsText = count === 1 ? singularText : pluralText;
        totalHitsEl.textContent = `${count} ${hitsText}`;
    }
}

const companyCards = async () => {
    const companyBlockEl = document.querySelector('.company-block');
    if (!companyBlockEl) return;
    
    const inputWrapperEl = document.querySelector('.input-search-wrapper') as HTMLInputElement;
    if (!inputWrapperEl) return;
    
    const inputEl = inputWrapperEl.querySelector('.input-cards-filter') as HTMLInputElement;
    const iconEl = inputWrapperEl.querySelector('.search-icon') as HTMLInputElement;
    const filterBoxEl = document.querySelector('.filter-box');

    if (!inputEl || !iconEl) return;

    const { lang, guuid } = (inputWrapperEl as HTMLElement).dataset;
    
    if (!guuid || !lang) {
        console.error('Missing guuid or lang data attributes on input-search-wrapper');
        return;
    }
    
    const langId = Number(lang);
    const mainUrl: string = window.location.origin;
    const companyListApiUrl: string = `${mainUrl}/api/map/CompanyListPageMap/${guuid}/${lang}`;

    const { catIds, searchTerm } = getUrlData();
    
    const allCompaniesData = await getData(companyListApiUrl);
    const searchData = await getSearchResults({ guuid: guuid || '', langId, catIds, searchTerm: searchTerm || '' });
    
    const filteredList = filterResults(allCompaniesData.mapItems, searchData);
    searchData.pageSearchHitsCount = filteredList.length;

    handleNoResultsMessage(filteredList.length < 1);
    updateTotalHits(filteredList.length);

    // Display initial results (first page)
    const initialPageSize = 12; // Default page size from Constants.Lists.NumberOfItemsInList
    const initialSlice = filteredList.slice(0, Math.min(initialPageSize, filteredList.length));
    createList(initialSlice);

    inputEl.value = searchTerm || '';
    inputEl.onkeyup = function (e) {
        const event = e as KeyboardEvent;
        if (event.key === 'Enter' && event.target) {
            const searchTerm: string = (event.target as HTMLInputElement).value;
            updateList({ searchTerm, guuid: guuid || '', langId, initialList: allCompaniesData.mapItems });
        }
    };
    iconEl.addEventListener('click', function () {
        updateList({ searchTerm: inputEl.value, guuid: guuid || '', langId, initialList: allCompaniesData.mapItems });
    });

    if (filterBoxEl) {
        const filterSubmitButtonEl = filterBoxEl.querySelector('.filter-button--submit');
        const filterAbortButtonEl = filterBoxEl.querySelector('.filter-button--abort');

        if (filterSubmitButtonEl) {
            filterSubmitButtonEl.addEventListener('click', function () {
                updateList({ searchTerm: searchTerm || '', guuid: guuid || '', langId, initialList: allCompaniesData.mapItems });
            });
        }
        if (filterAbortButtonEl) {
            filterAbortButtonEl.addEventListener('click', function () {
                updateList({ searchTerm: searchTerm || '', guuid: guuid || '', langId, initialList: allCompaniesData.mapItems });
            });
        }
    }

    cardsPagination(searchData, (pagination) => handlePageChange(pagination, filteredList));
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', companyCards);
} else {
    companyCards();
}
